import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import { UI } from '../utils/arena/uiSounds';
import { BOOSTER_SETS } from '../utils/arena/packsApi';
import {
  getDraftEvent, leaveDraftEvent, cancelDraftEvent, startDraftEarly, inviteToDraft,
  getDraftPickHistory,
  subscribeToDraftEvents, DRAFT_STATUS,
} from '../utils/arena/draftApi';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, COIN_COLOR,
  BEVELED_BTN, GOLD_BTN, DANGER_BTN,
  DIALOG_STYLE, INPUT_STYLE,
  FourCorners, OrnamentalDivider, RADIUS,
} from '../lib/medievalTheme';
import { t } from '../localization/t';
import DeckCardTile from './DeckCardTile';
import { CloseButton } from './ui/navButtons';
import { Select } from './ui/select';
import { CoinIcon } from './ui/icons';
import { resolvePrinting } from '../utils/sorcery/foil';

export default class DraftLobby extends Component {
  constructor(props) {
    super(props);
    this.state = {
      event: null,
      loading: true,
      error: null,
      countdown: '',
      showInvite: false,
      invitedIds: new Set(),
      leaving: false,
      cancelling: false,
      starting: false,
    };
    this.countdownTimer = null;
  }

  componentDidMount() {
    this.unsubDraft = subscribeToDraftEvents({
      joined: () => this.loadEvent(),
      left: () => this.loadEvent(),
      cancelled: (data) => {
        if (data?.eventId === this.props.eventId) {
          this.props.onCancelled?.();
        }
      },
      started: (data) => {
        if (data?.eventId === this.props.eventId) {
          this.props.onDraftStarted?.(data);
        }
      },
      // Sealed skips the picker entirely. The server auto-records
      // the full 90-card pool at draft-start and fires building
      // immediately. Forward the event up so app.jsx can route
      // straight into DraftDeckBuilder without ever mounting
      // DraftPicker.
      building: (data) => {
        if (data?.eventId === this.props.eventId) {
          this.props.onBuildingPhase?.(data);
        }
      },
    });
    this.loadEvent();
    this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
    // Defensive poll: if a WS event gets dropped (socket blip right
    // as draft:started / draft:building fires), we'd otherwise sit
    // at "Starting…" forever while the other seat moves on. Every
    // 2s we re-fetch the event; once status leaves 'open', we
    // trigger the appropriate phase transition ourselves.
    this.statusPollTimer = setInterval(() => this.pollEventStatus(), 2000);
  }

  pollEventStatus = async () => {
    if (this._unmounted) return;
    try {
      const event = await getDraftEvent(this.props.eventId);
      if (!event || this._unmounted) return;
      this.setState({ event });
      // Sealed: 'building' is the phase the server transitions to
      // immediately after auto-recording the pool. The user should
      // be in the pack-opening ceremony. Pull the player's picks
      // and hand them up via onBuildingPhase as if the WS event
      // had arrived.
      if (event.mode === 'sealed' && event.status === 'building' && !this._firedBuilding) {
        this._firedBuilding = true;
        try {
          const response = await getDraftPickHistory(this.props.eventId);
          const picks = response?.picks || [];
          this.props.onBuildingPhase?.({
            eventId: event.id,
            draftedCards: picks,
            setKey: event.setKey,
            mode: 'sealed',
          });
        } catch (err) {
          console.warn('[DraftLobby] sealed picks fetch failed:', err);
          this._firedBuilding = false;
        }
      } else if (event.status === 'drafting' && !this._firedStarted) {
        this._firedStarted = true;
        this.props.onDraftStarted?.({ eventId: event.id, mode: event.mode });
      } else if (event.status === 'building' && !this._firedBuilding) {
        // Standard draft that returned to the lobby after the pod
        // finished picking. The live `draft:building` WS event the
        // player would have received during the pick→build
        // transition carried their picks; on a fresh resume that
        // event is gone, so we have to fetch the pick history
        // explicitly. Without this fetch the deck builder mounts
        // with an empty pool and the player only sees the default
        // Spellslinger avatar.
        this._firedBuilding = true;
        try {
          const response = await getDraftPickHistory(this.props.eventId);
          const picks = response?.picks || [];
          this.props.onBuildingPhase?.({
            eventId: event.id,
            draftedCards: picks,
            setKey: event.setKey,
            mode: event.mode,
          });
        } catch (err) {
          console.warn('[DraftLobby] standard-draft picks fetch failed:', err);
          this._firedBuilding = false;
        }
      } else if (event.status === 'tournament' && !this._firedTournament) {
        // Resuming into a draft that's already in Swiss rounds.
        // Fire the same callback draft:round_start would; app.jsx's
        // handleDraftTournamentStart routes to the tournament view.
        // Pairings / myRoomId get pulled by DraftTournament on mount
        // via getDraftStandings, so we don't need them here.
        this._firedTournament = true;
        this.props.onTournamentStart?.({ eventId: event.id, mode: event.mode });
      } else if (event.status === 'complete' && !this._firedComplete) {
        // Draft finished while the user was away. Route them to
        // the results view so they can see the final standings +
        // any prize card they earned.
        this._firedComplete = true;
        this.props.onDraftComplete?.({ eventId: event.id, mode: event.mode });
      } else if (event.status === 'cancelled') {
        this.props.onCancelled?.();
      }
    } catch {}
  };

  componentWillUnmount() {
    this.unsubDraft?.();
    clearInterval(this.countdownTimer);
    clearInterval(this.statusPollTimer);
    this._unmounted = true;
  }

  loadEvent = async () => {
    try {
      const event = await getDraftEvent(this.props.eventId);
      this.setState({ event, loading: false });
      this.updateCountdown();
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  updateCountdown() {
    const { event } = this.state;
    if (!event?.scheduledAt) return;

    const diff = new Date(event.scheduledAt) - Date.now();
    if (diff <= 0) {
      this.setState({ countdown: 'Starting...' });
      return;
    }

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    let text = '';
    if (hours > 0) text += `${hours}h `;
    text += `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    this.setState({ countdown: text });
  }

  handleLeave = async () => {
    this.setState({ leaving: true });
    try {
      await leaveDraftEvent(this.props.eventId);
      this.props.onLeft?.();
    } catch (err) {
      this.setState({ leaving: false });
      console.error('[DraftLobby] leave failed:', err);
    }
  };

  handleCancel = async () => {
    this.setState({ cancelling: true });
    try {
      await cancelDraftEvent(this.props.eventId);
      this.props.onCancelled?.();
    } catch (err) {
      this.setState({ cancelling: false });
      console.error('[DraftLobby] cancel failed:', err);
    }
  };

  handleStartEarly = async () => {
    this.setState({ starting: true });
    try {
      await startDraftEarly(this.props.eventId);
    } catch (err) {
      this.setState({ starting: false });
      console.error('[DraftLobby] start early failed:', err);
    }
  };

  handleInviteFriend = async (friendId) => {
    try {
      await inviteToDraft(this.props.eventId, [friendId]);
      this.setState((s) => {
        const next = new Set(s.invitedIds);
        next.add(friendId);
        return { invitedIds: next };
      });
    } catch (err) {
      console.error('[DraftLobby] invite failed:', err);
    }
  };

  getInvitableOptions() {
    const { friendListData } = this.props;
    const { event, invitedIds } = this.state;
    const friends = friendListData?.friends || [];
    const participantIds = new Set((event?.participants || []).map((p) => p.playerId));

    return friends
      .filter((f) => !participantIds.has(f.id))
      .map((f) => ({
        value: f.id,
        label: f.name || t('Unknown'),
        disabled: invitedIds.has(f.id),
        description: invitedIds.has(f.id) ? t('Invited') : undefined,
      }));
  }

  resolvePrizeCard(prizeCard) {
    if (!prizeCard) return null;
    const sorceryCards = this.props.sorceryCards || [];
    const card = sorceryCards.find((c) => c.unique_id === prizeCard.cardId);
    if (!card) return null;
    const printing = resolvePrinting(card, prizeCard.printingId, 'F');
    return { card, printing, rarity: prizeCard.rarity || card.rarity };
  }

  renderPrizeCard(prizeCard) {
    const resolved = this.resolvePrizeCard(prizeCard);
    if (!resolved) return null;

    const RARITY_COLORS = { Unique: '#e8c840', Elite: '#c860e0', Exceptional: '#4898e0', Ordinary: TEXT_BODY, Avatar: '#e04040' };

    return (
      <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded" style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${GOLD} 0.15)` }}>
        <div style={{ width: '2.5cqw', height: Math.round(40 * 88 / 63), borderRadius: RADIUS.card, overflow: 'hidden', flexShrink: 0 }}>
          <DeckCardTile
            entry={{ card: resolved.card, printing: resolved.printing || {}, zone: 'spellbook', entryIndex: 0 }}
            isSelected={false}
            onClick={() => {}}
            onHoverChange={() => {}}
          />
        </div>
        <div className="min-w-0">
          <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('1st Place Prize')}</div>
          <div className="text-xs font-semibold truncate" style={{ color: RARITY_COLORS[resolved.rarity] || TEXT_PRIMARY }}>
            {resolved.card.name}
          </div>
          <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('{rarity} Foil', { rarity: resolved.rarity })}</div>
        </div>
      </div>
    );
  }

  render() {
    const { profile } = this.props;
    const { event, loading, error, countdown, showInvite, leaving } = this.state;

    if (loading) {
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <RuneSpinner size={64} />
        </div>
      );
    }

    if (error || !event) {
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="text-center p-8" style={DIALOG_STYLE}>
            <FourCorners knots />
            <div className="text-sm mb-4" style={{ color: '#c45050' }}>{error || t('Event not found')}</div>
            <CloseButton onClick={this.props.onClose} />
          </div>
        </div>
      );
    }

    const setLabel = BOOSTER_SETS[event.setKey]?.label || event.setKey;
    const participants = event.participants || [];
    const slots = Array.from({ length: event.podSize }, (_, i) => participants[i] || null);
    const isSealed = event.mode === 'sealed';
    // Sealed and Draft use the same lobby UI but the copy diverges.
    // sealed is a 1v1 friend match, draft is a pod-and-Swiss event.
    const modeLabel = isSealed ? t('Sealed') : t('Draft');

    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
        <div className="relative w-full max-w-[36cqw] p-8" style={{ ...DIALOG_STYLE }}>
          <FourCorners knots />

          {/* X button. Close modal, stay in queue */}
          <button
            type="button"
            className="absolute top-3 right-3 w-7 h-7 rounded-md flex items-center justify-center transition-colors cursor-pointer z-10"
            style={{ color: TEXT_MUTED }}
            onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_BODY; e.currentTarget.style.background = `${GOLD} 0.08)`; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; e.currentTarget.style.background = 'transparent'; }}
            data-sound={UI.CLOSE}
            onClick={this.props.onClose}
            title={t('Close (stay in queue)')}
          >
            <svg viewBox="0 0 14 14" fill="none" style={{ width: '0.875cqw', height: '0.875cqw' }}>
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>

            {/* Header */}
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold arena-heading" style={{ color: ACCENT_GOLD, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.15)' }}>
                {t('{set} {mode}', { set: setLabel, mode: modeLabel })}
              </h2>
              <div className="text-xs mt-1 flex items-center justify-center gap-3" style={{ color: TEXT_MUTED }}>
                <span>{event.visibility === 'private' ? t('private event') : t('public event')}</span>
                <span className="flex items-center gap-0.5" style={{ color: COIN_COLOR }}>
                  {event.entryCost}<CoinIcon size={10} /> {t('entry')}
                </span>
              </div>
            </div>
            <OrnamentalDivider className="mb-4" />

            {/* Waiting-for-players status. Draft start used to be
                timer-gated ("Starting in 04m 35s") which surfaced
                the scheduledAt field; the unified flow tosses
                that out. Drafts start when the pod fills and
                every participant confirms the ready-check modal.
                Just show a neutral waiting hint here. */}
            {(() => {
              const filled = (event.participants || []).length;
              const pod = event.podSize || 0;
              const needed = Math.max(0, pod - filled);
              return (
                <div className="text-center mb-5">
                  <div className="text-xs" style={{ color: TEXT_MUTED }}>
                    {needed > 0
                      ? (needed === 1 ? t('Waiting for 1 more player') : t('Waiting for {n} more players', { n: needed }))
                      : t('Pod full. Ready-check incoming')}
                  </div>
                </div>
              );
            })()}

            {/* Player slots */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {slots.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 rounded"
                  style={{
                    background: p ? 'rgba(212,168,67,0.08)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${p ? `${GOLD} 0.2)` : 'rgba(255,255,255,0.05)'}`,
                  }}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[0.625cqw] font-bold"
                    style={{
                      background: p ? `${GOLD} 0.15)` : 'rgba(255,255,255,0.05)',
                      color: p ? ACCENT_GOLD : TEXT_MUTED,
                    }}
                  >
                    {i + 1}
                  </div>
                  <span className="text-sm truncate" style={{ color: p ? TEXT_PRIMARY : TEXT_MUTED }}>
                    {p ? (p.playerName || p.playerId) : t('Waiting...')}
                  </span>
                </div>
              ))}
            </div>

            <OrnamentalDivider className="mb-4" />

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              {event.creatorId === profile?.id && participants.length >= 2 ? (
                <button
                  type="button"
                  className="px-5 py-2 text-sm font-semibold transition-all cursor-pointer"
                  style={GOLD_BTN}
                  data-sound={UI.CONFIRM}
                  disabled={this.state.starting}
                  onClick={this.handleStartEarly}
                >
                  {this.state.starting ? t('Starting...') : t('Start {mode} Now', { mode: modeLabel })}
                </button>
              ) : null}
              {event.visibility === 'private' || event.creatorId === profile?.id ? (
                <button
                  type="button"
                  className="px-4 py-2 text-sm transition-all cursor-pointer"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY }}
                  data-sound={UI.TAB}
                  onClick={() => this.setState({ showInvite: !showInvite })}
                >
                  {t('Invite Friends')}
                </button>
              ) : null}
              {event.creatorId === profile?.id ? (
                <button
                  type="button"
                  className="px-4 py-2 text-sm transition-all cursor-pointer"
                  style={DANGER_BTN}
                  data-sound={UI.CANCEL}
                  disabled={this.state.cancelling}
                  onClick={this.handleCancel}
                >
                  {this.state.cancelling ? t('Cancelling...') : t('Cancel Event')}
                </button>
              ) : (
                <button
                  type="button"
                  className="px-4 py-2 text-sm transition-all cursor-pointer"
                  style={DANGER_BTN}
                  data-sound={UI.CANCEL}
                  disabled={leaving}
                  onClick={this.handleLeave}
                >
                  {leaving ? t('Leaving...') : t('Leave Event')}
                </button>
              )}
            </div>

            {/* Invite panel */}
            {showInvite ? (
              <div className="mt-4 p-3 rounded" style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${GOLD} 0.15)` }}>
                <label className="text-[0.625cqw] font-semibold block mb-1" style={{ color: `${GOLD} 0.55)` }}>
                  {t('Invite a friend')}
                </label>
                <Select
                  ariaLabel={t('Invite friend')}
                  options={this.getInvitableOptions()}
                  value=""
                  onValueChange={(friendId) => {
                    if (friendId) this.handleInviteFriend(friendId);
                  }}
                  placeholder={t('Choose a friend...')}
                  searchable
                  menuSearchPlaceholder={t('Type to filter friends...')}
                  noOptionsMessage={t('No friends to invite')}
                />
                {this.state.invitedIds.size > 0 ? (
                  <div className="mt-2 text-[0.625cqw]" style={{ color: '#6dba6d' }}>
                    {this.state.invitedIds.size === 1 ? t('1 invite sent') : t('{n} invites sent', { n: this.state.invitedIds.size })}
                  </div>
                ) : null}
              </div>
            ) : null}
        </div>
      </div>
    );
  }
}
